import { HttpEventType, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Court } from '@app/core/models';
import { GenericActionResult } from '@app/core/models/generic-action-result.model';
import { State } from '@app/core/models/state-list.model';
import { WebApiService } from '@app/core/services/web-api.service';
import { BehaviorSubject } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { tap } from 'rxjs/operators';
import { WorkQueueItem } from './models/work-queue-item';
import { environment } from '@env/environment';


export interface ConciergeOrder {
    workOrderId: number;
    firmId: number;
    state: number;
    court: string;
}

@Injectable({
    providedIn: 'root',
})
export class ConciergeService {
    currentWorkOrder = new BehaviorSubject<WorkOrder>(null);

    immediate = new BehaviorSubject<WorkQueueItem[]>(null);
    rush = new BehaviorSubject<WorkQueueItem[]>(null);
    standard = new BehaviorSubject<WorkQueueItem[]>(null);

    courts: Court[];

    progress = 0;

    awsStorageSettings = {
        bucketName: "efilingprod",
        hostUrl: environment.awsBucket,
    };

    constructor(private webApiService: WebApiService) { }

    clearWorkOrder = (): void => this.currentWorkOrder.next(null);

    postConciergeOrder(formData: FormData): Observable<GenericActionResult> {
        return this.webApiService.postFormData('/concierge/order', formData);
    }

    postConciergeOrderWithProgress(formData: FormData): Observable<GenericActionResult> {
        const options = {
            reportProgress: true,
            observe: 'events'
        };

        this.progress = 0;

        return this.webApiService.postFormDataWithOptions('/concierge/order', formData, options)
            .pipe(tap(event => {
                if (event.type == HttpEventType.UploadProgress) {
                    this.progress = Math.round(100 * event.loaded / event.total);
                }
            }));
    }

    firmUsersForWorkQueue = new BehaviorSubject<GenericActionResult>(null);

    getFirmUsersForWorkQueue(): Observable<GenericActionResult> {
        return this.webApiService.get('/concierge/work-queue/firm-users')
            .pipe(tap(results => this.firmUsersForWorkQueue.next(results)));
    }

    getWorkQueue(): Observable<GenericActionResult> {
        return this.webApiService
            .get('/concierge/work-queue');
    }

    getWorkHistory(query: ConciergeWorkHistoryQuery): Observable<GenericActionResult> {
        const {
            transactionNumber,
            alnOrderNumber,
            caseNumber,
            from,
            to,
            firmUser,
        } = query;

        let params = new HttpParams();

        if (transactionNumber) {
            params = params.append('transactionNumber', transactionNumber);
        }

        if (alnOrderNumber) {
            params = params.append('alnOrderNumber', '' + alnOrderNumber);
        }

        if (caseNumber) {
            params = params.append('caseNumber', caseNumber);
        }

        if (from) {
            params = params.append('from', from.toDateString());
        }

        if (to) {
            params = params.append('to', '' + to.toDateString());
        }

        if (firmUser) {
            params = params.append('firmUser', firmUser);
        }
        return this.webApiService.get('/concierge/work-history', params);
    }

    cancelWorkItem(orderId: number, reason: string): Observable<GenericActionResult> {
        return this.webApiService.post('/concierge/cancel-order',
            { orderId, reason });
    }

    getConciergeCreditCard(workOrderId: number): Observable<GenericActionResult> {
        return this.webApiService.get(`/concierge/order-card/${workOrderId}`);
    }
}

export interface ConciergeWorkHistoryQuery {
    transactionNumber?: string;
    alnOrderNumber?: number;
    caseNumber?: string;
    from?: Date;
    to?: Date;
    firmUser?: string;
}


export interface WorkOrder {
    conciergeId: number;
    courtOrderId?: number;
    workQueueItem?: WorkQueueItem;
    state?: State;
    court?: Court;
}